import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import {
  Home,
  User,
  Users,
  Calendar,
  Heart,
  Flame,
  Bell,
  Menu,
  X,
  LogOut,
  ChevronDown,
  FileText,
  BookOpen,
  Armchair,
  GraduationCap,
  Landmark,
} from 'lucide-react';

interface PortalLayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onNavigate: (page: 'dashboard' | 'profile' | 'directory' | 'events' | 'donate' | 'yahrzeits' | 'notifications' | 'billing' | 'honors' | 'my-seats' | 'school' | 'cemetery') => void;
  user: {
    organization: { name: string; logoUrl?: string | null };
    member?: { firstName: string; lastName: string; photoUrl?: string | null } | null;
    email: string;
  };
}

const navigation = [
  { name: 'Dashboard', page: 'dashboard' as const, icon: Home },
  { name: 'Events', page: 'events' as const, icon: Calendar },
  { name: 'Donate', page: 'donate' as const, icon: Heart },
  { name: 'Billing', page: 'billing' as const, icon: FileText },
  { name: 'Yahrzeits', page: 'yahrzeits' as const, icon: Flame },
  { name: 'Honors', page: 'honors' as const, icon: BookOpen },
  { name: 'My Seats', page: 'my-seats' as const, icon: Armchair },
  { name: 'School', page: 'school' as const, icon: GraduationCap },
  { name: 'Cemetery', page: 'cemetery' as const, icon: Landmark },
  { name: 'My Profile', page: 'profile' as const, icon: User },
  { name: 'Directory', page: 'directory' as const, icon: Users },
];

export function PortalLayout({ children, currentPage, onNavigate, user }: PortalLayoutProps) {
  const { logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const displayName = user.member
    ? `${user.member.firstName} ${user.member.lastName}`
    : user.email;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo & Org Name */}
            <div className="flex items-center gap-3">
              {user.organization.logoUrl ? (
                <img
                  src={user.organization.logoUrl}
                  alt={user.organization.name}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white text-sm font-semibold">
                  {user.organization.name.charAt(0)}
                </div>
              )}
              <span className="text-lg font-semibold text-gray-900 hidden sm:block">
                {user.organization.name}
              </span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navigation.map((item) => (
                <button
                  key={item.page}
                  onClick={() => onNavigate(item.page)}
                  className={cn(
                    'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    currentPage === item.page
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.name}
                </button>
              ))}
            </nav>

            {/* User Menu & Mobile Toggle */}
            <div className="flex items-center gap-2">
              {/* User Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                >
                  {user.member?.photoUrl ? (
                    <img
                      src={user.member.photoUrl}
                      alt=""
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-300 text-gray-600 text-xs font-medium">
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="hidden sm:block max-w-32 truncate">{displayName}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>

                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 z-20 mt-2 w-48 rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          onNavigate('profile');
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <User className="h-4 w-4" />
                        My Profile
                      </button>
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          onNavigate('notifications');
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Bell className="h-4 w-4" />
                        Notifications
                      </button>
                      <hr className="my-1 border-gray-200" />
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          logout();
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden rounded-lg p-2 text-gray-600 hover:bg-gray-100"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <nav className="px-4 py-3 space-y-1">
              {navigation.map((item) => (
                <button
                  key={item.page}
                  onClick={() => {
                    onNavigate(item.page);
                    setMobileMenuOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    currentPage === item.page
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </button>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {children}
      </main>
    </div>
  );
}
